import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Numeric, Typography } from '@blade/ui-kit'
import { PairTrend } from '@root/entities/pair/ui'
import { PairLiquidityFormatted } from '@root/entities/pair/ui/common/pair-liquidity-formatted'
import {
  useWalletsAssetBalance,
  useWalletsAssetBalanceUsd,
} from '@root/entities/wallet/hooks'
import { AmountFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
}

export const FavoritePairsListInfo: React.FC<Props> = ({ pair }) => {
  const { t } = useTranslation()

  const [volumeUsd] = useAtom(pair.stats.ALL.volumeUsd)
  const [priceChange] = useAtom(pair.stats['5M'].priceChange)
  const assetBalance = useWalletsAssetBalance({
    currency: pair.quote,
  })
  const assetBalanceUsd = useWalletsAssetBalanceUsd({
    currency: pair.quote,
  })

  return (
    <Root>
      <Col>
        <Label>{t('common.mcap-short')}</Label>
        <Value>
          <Numeric
            value={volumeUsd}
            preset="currency-amount-short"
            currency="$"
          />
          <Trend
            changePercents={priceChange.toNumber()}
            showPercents
            iconSize={12}
            iconVariant="triangle"
          />
        </Value>
      </Col>
      <Col>
        <Label>{t('pool.liquidity')}</Label>
        <Value>
          <PairLiquidityFormatted pair={pair} />
        </Value>
      </Col>
      <Col>
        <Label>{t('common.holding')}</Label>
        <Value>
          <AmountFormatted amount={assetBalance} roundingMode="trunc" />
        </Value>
        <ValueSecondary>
          <AmountFormatted
            amount={assetBalanceUsd}
            currency="$"
            roundingMode="trunc"
          />
        </ValueSecondary>
      </Col>
      <Col>
        <Label>{t('common.5m-percent')}</Label>
        <Value>
          <Numeric value={priceChange} preset="trend-change-percents" />%
        </Value>
      </Col>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 4px;
  align-items: start;
`

const Col = styled.span`
  display: grid;
  justify-items: start;
`

const Label = styled(Typography).attrs({
  variant: 'body5-medium',
  component: 'span',
})`
  color: var(--textA-8);

  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 1px;
`

const Value = styled(Typography).attrs({
  variant: 'body4-medium',
  component: 'span',
})`
  color: var(--text-11);
`

const ValueSecondary = styled(Value).attrs({
  variant: 'description-medium',
  component: 'span',
})`
  color: var(--text-9);
`

const Trend = styled(PairTrend)`
  font-size: var(--typography-description-medium-font-size);
  font-weight: var(--typography-description-medium-font-weight);
  line-height: var(--typography-description-medium-line-height);
  justify-content: start;
  gap: 1px;
`
